import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { sq, useTitle } from "@/lib/img";
import { MaskedLines, FadeUp, EASE } from "@/components/Reveal";

export default function Projects() {
  useTitle(
    "Projects | Medium Plenty",
    "The portfolio of Medium Plenty — residential, hospitality, and workplace architecture and interior design projects across the U.S. and abroad."
  );
  const [projects, setProjects] = useState([]);
  const [filter, setFilter] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  const categories = ["All", "Residential", "Hospitality", "Workplace"].filter(
    (c) => c === "All" || projects.some((p) => p.category === c)
  );
  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page" className="pt-32 md:pt-44 px-6 md:px-12 pb-28 md:pb-40 min-h-screen">
      <h1 className="font-serif font-light text-[12vw] md:text-[7vw] leading-[0.95] tracking-tight mb-8">
        <MaskedLines lines={["Projects"]} delay={0.15} />
      </h1>
      <FadeUp delay={0.2} className="mb-14 md:mb-20 max-w-2xl">
        <p className="font-serif font-light text-sm md:text-base leading-[1.4] tracking-tight">
          “I like to use light as a material, but my medium is actually perception. I want you to sense yourself
          sensing — to see yourself seeing.”
        </p>
        <p className="kicker mt-5">— James Turrell</p>
      </FadeUp>

      <FadeUp className="flex flex-wrap gap-x-8 gap-y-3 mb-14 md:mb-20" delay={0.2}>
        {categories.map((c) => (
          <button
            key={c}
            data-testid={`filter-btn-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => setFilter(c)}
            className={`editorial-link text-[13px] tracking-[0.1em] uppercase transition-colors duration-300 ${
              filter === c ? "text-ink" : "text-smoke/60 hover:text-ink"
            }`}
            data-active={filter === c}
          >
            {c}
          </button>
        ))}
      </FadeUp>

      <div data-testid="projects-list">
        <AnimatePresence mode="popLayout">
          {visible.map((p, i) => (
            <motion.div
              key={p.slug}
              layout
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.04 }}
            >
              <button
                data-testid={`project-row-${p.slug}`}
                onClick={() => navigate(`/projects/${p.slug}`)}
                className="group relative w-full text-left border-t border-line last:border-b py-7 md:py-10 grid grid-cols-12 items-center gap-4 md:gap-6"
              >
                <span className="col-span-3 md:col-span-2 overflow-hidden bg-sand">
                  <img
                    src={sq(p.hero_image, 500)}
                    alt={p.name}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover"
                  />
                </span>
                <span className="col-span-9 md:col-span-6 font-serif font-light text-xl md:text-4xl leading-tight tracking-tight transition-transform duration-700 ease-out group-hover:translate-x-3">
                  {p.name}
                </span>
                <span className="col-span-9 col-start-4 md:col-span-4 md:col-start-auto text-sm text-smoke">
                  {p.category} — {p.location}
                </span>
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
